export const SYNTHETIC_SLOTS = [
  "2030-01-15T15:00:00.000Z",
  "2030-01-15T17:00:00.000Z",
  "2030-01-16T16:00:00.000Z",
] as const;

export type TraceStage =
  | "request"
  | "planner"
  | "tool"
  | "guard"
  | "mutation"
  | "outcome";

export type TraceStatus =
  | "info"
  | "pending"
  | "allowed"
  | "blocked"
  | "success"
  | "replayed"
  | "error";

export type TraceValue = string | number | boolean | null | readonly string[];

export interface TraceEvent {
  sequence: number;
  at: string;
  stage: TraceStage;
  status: TraceStatus;
  label: string;
  data?: Readonly<Record<string, TraceValue>>;
}

export interface AgentRequest {
  sessionId: string;
  message: string;
  idempotencyKey: string;
  requestedSlot?: string;
  confirmation?: {
    action: "book_appointment";
    sessionId: string;
    slot: string;
    token: string;
  };
}

export interface AvailabilitySnapshot {
  version: number;
  slots: readonly string[];
}

export interface BookingRecord {
  bookingId: string;
  sessionId: string;
  slot: string;
  idempotencyKey: string;
}

export interface IdempotencyRecord {
  key: string;
  fingerprint: string;
  booking: BookingRecord;
}

export interface FindSlotsCall {
  id: string;
  name: "find_slots";
  input: Readonly<Record<string, never>>;
}

export interface BookAppointmentCall {
  id: string;
  name: "book_appointment";
  input: {
    sessionId: string;
    slot: string;
    expectedVersion: number;
    idempotencyKey: string;
  };
}

export type ToolCall = FindSlotsCall | BookAppointmentCall;

export interface AvailabilityToolResult {
  callId: string;
  name: "find_slots";
  status: "success";
  snapshot: AvailabilitySnapshot;
}

export type GuardReason =
  | "invalid_idempotency_key"
  | "missing_explicit_confirmation"
  | "idempotency_conflict"
  | "stale_snapshot"
  | "slot_not_available";

export interface BookingToolResult {
  callId: string;
  name: "book_appointment";
  status: "booked" | "replayed" | "blocked" | "failed";
  booking?: BookingRecord;
  reason?: GuardReason | "adapter_rejected_mutation";
}

export type ToolResult = AvailabilityToolResult | BookingToolResult;

export type AgentOutcome =
  | {
      kind: "availability_presented";
      availableSlots: readonly string[];
      snapshotVersion: number;
    }
  | {
      kind: "appointment_booked";
      bookingId: string;
      slot: string;
      replayed: boolean;
    }
  | {
      kind: "mutation_blocked";
      tool: "book_appointment";
      reason: GuardReason | "adapter_rejected_mutation";
    }
  | {
      kind: "failed";
      code: "planner_exhausted" | "invalid_request";
    };

export interface PlannerContext {
  request: AgentRequest;
  toolResults: readonly ToolResult[];
}

export type PlannerAction =
  | {
      type: "tool_call";
      call: ToolCall;
    }
  | {
      type: "respond";
      reply: string;
      outcome: AgentOutcome;
    };

export interface Planner {
  next(context: PlannerContext): Promise<PlannerAction>;
}

export interface AgentRunResult {
  reply: string;
  outcome: AgentOutcome;
  trace: readonly TraceEvent[];
}

export interface GuardAllowed {
  allowed: true;
  replay: boolean;
}

export interface GuardBlocked {
  allowed: false;
  reason: GuardReason;
}

export type GuardDecision = GuardAllowed | GuardBlocked;

export interface BookingMutationInput {
  sessionId: string;
  slot: string;
  expectedVersion: number;
  idempotencyKey: string;
}

export interface BookingMutationResult {
  booking: BookingRecord;
  replayed: boolean;
}
